// Range presets for the AI Checker window selector.
export type AiCheckerRange = 'day' | 'week' | 'month' | 'all';

/** YYYY-MM-DD in LOCAL time — `toISOString()` would shift the date by the UTC offset. */
export const fmtLocalDate = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/**
 * Resolve a range + period offset (0 = current, -1 = previous, …) into concrete dates + a label.
 * Day = a single calendar day, Week = a Monday–Sunday calendar week, Month = a calendar month, All = everything.
 */
export function rangeToDates(range: AiCheckerRange, offset: number): { startDate: string; endDate: string; label: string } {
  const today = new Date();
  if (range === 'all') {
    return { startDate: '2000-01-01', endDate: fmtLocalDate(today), label: 'All time' };
  }
  if (range === 'day') {
    const d = new Date(today);
    d.setDate(today.getDate() + offset);
    const s = fmtLocalDate(d);
    return { startDate: s, endDate: s, label: d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) };
  }
  if (range === 'week') {
    // Monday-to-Sunday calendar week containing (today + offset weeks)
    const start = new Date(today);
    start.setDate(today.getDate() - ((today.getDay() + 6) % 7) + 7 * offset);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    const opts: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short' };
    return {
      startDate: fmtLocalDate(start),
      endDate: fmtLocalDate(end),
      label: `${start.toLocaleDateString(undefined, opts)} – ${end.toLocaleDateString(undefined, opts)}`,
    };
  }
  // month — calendar month containing (today + offset months)
  const ref = new Date(today.getFullYear(), today.getMonth() + offset, 1);
  const start = new Date(ref.getFullYear(), ref.getMonth(), 1);
  const end = new Date(ref.getFullYear(), ref.getMonth() + 1, 0);
  return {
    startDate: fmtLocalDate(start),
    endDate: fmtLocalDate(end),
    label: ref.toLocaleDateString(undefined, { month: 'long', year: 'numeric' }),
  };
}

export const AI_CHECKER_RANGES: { key: AiCheckerRange; labelKey: string }[] = [
  { key: 'day', labelKey: 'adminTesting.review.rangeDay' },
  { key: 'week', labelKey: 'adminTesting.review.rangeWeek' },
  { key: 'month', labelKey: 'adminTesting.review.rangeMonth' },
  { key: 'all', labelKey: 'adminTesting.review.rangeAll' },
];
